import { Select } from "@headlessui/react"
import { useSearch } from "@tanstack/react-router"
import classNames from "classnames"
import React from "react"

import { useSubscribers } from "@/hooks"

interface Props {
  defaultDisabled?: boolean
  defaultOption?: string
  defaultValue?: string
  disabled?: boolean
  hasError?: boolean
  id: string
  name?: string
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
  showOptionAll?: boolean
  showOptionSelect?: boolean
  value?: string
}

const SubscriberSelect: React.FC<Props> = ({
  defaultDisabled,
  defaultOption,
  defaultValue,
  disabled,
  hasError,
  id,
  name,
  onBlur,
  onChange,
  value,
}) => {
  const { subscriberId, partnerId } = useSearch({
    strict: false,
  })

  const {
    data: subscribers,
    isLoading,
    isError,
  } = useSubscribers(undefined, partnerId)

  if (isLoading) {
    return (
      <Select disabled>
        <option disabled>Loading...</option>
      </Select>
    )
  }

  if (isError) {
    return (
      <Select disabled>
        <option disabled>Error loading subscribers</option>
      </Select>
    )
  }

  const classnames = classNames({
    "w-full bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5":
      true,
    "border-red-300": hasError === true,
    "border-gray-300": hasError !== true,
  })

  return (
    <Select
      aria-label="Subscriber"
      className={classnames}
      disabled={disabled}
      id={id}
      name={name ?? "subscriberId"}
      onBlur={onBlur}
      onChange={onChange}
      value={(value || subscriberId) ?? defaultValue}
    >
      {defaultOption && (
        <option value={defaultValue} disabled={defaultDisabled}>
          {defaultOption}
        </option>
      )}
      {subscribers
        ?.sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
        .map((subscriber, key) => (
          <option key={key} value={subscriber.id}>
            {subscriber.name}
          </option>
        ))}
    </Select>
  )
}

export default SubscriberSelect
